import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import Grid from "@mui/material/Grid"
import Paper from "@mui/material/Paper"
import CircularProgress from "@mui/material/CircularProgress"
import { useEffect, useState } from "react"
import {
	adminGetUserCountV2,
	adminGetPostsCountByStatusV2,
	adminGetBidsCountV2,
	adminGetDealsCountV2,
	adminGetQuestionsCountV2,
	adminGetRevenueV2,
} from "../util/firebase"

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts"
import { useNavigate } from "react-router-dom"

const Dashboard = () => {
	const [loadingState, setLoadingState] = useState({
		userCount: true,
		postsCount: true,
		bidsCount: true,
		dealsCount: true,
		questionsCount: true,
		revenue: true,
	})
	const [dashboardState, setDashboardState] = useState("all")
	const [userCount, setUserCount] = useState(0)
	const [verifiedUserCount, setVerifiedUserCount] = useState(0)
	const [postsCount, setPostsCount] = useState({})
	const [bidsCount, setBidsCount] = useState(0)
	const [buyNowDealsCount, setBuyNowDealsCount] = useState(0)
	const [auctionDealsCount, setAuctionDealsCount] = useState(0)
	const [noDealsCount, setNoDealsCount] = useState(0)
	const [pendingQuestionsCount, setPendingQuestionsCount] = useState(0)
	const [activeQuestionsCount, setActiveQuestionsCount] = useState(0)
	const [revenue, setRevenue] = useState(0)

	const navigate = useNavigate()

	useEffect(() => {
		adminGetUserCountV2()
			.then((response) => {
				setUserCount(response.data.userCount)
				setVerifiedUserCount(response.data.verifiedUserCount)
				setLoadingState((prevState) => {
					return { ...prevState, userCount: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})

		adminGetPostsCountByStatusV2()
			.then((response) => {
				setPostsCount(response.data.postsCount)
				setLoadingState((prevState) => {
					return { ...prevState, postsCount: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})

		adminGetBidsCountV2()
			.then((response) => {
				setBidsCount(response.data.bidsCount)
				setLoadingState((prevState) => {
					return { ...prevState, bidsCount: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})

		adminGetDealsCountV2()
			.then((response) => {
				setBuyNowDealsCount(response.data.buyNowCount)
				setAuctionDealsCount(response.data.auctionCount)
				setNoDealsCount(response.data.noDealCount)
				setLoadingState((prevState) => {
					return { ...prevState, dealsCount: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})

		adminGetQuestionsCountV2()
			.then((response) => {
				setPendingQuestionsCount(response.data.pending)
				setActiveQuestionsCount(response.data.active)
				setLoadingState((prevState) => {
					return { ...prevState, questionsCount: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})

		adminGetRevenueV2()
			.then((response) => {
				setRevenue(response.data.revenue)
				setLoadingState((prevState) => {
					return { ...prevState, revenue: false }
				})
			})
			.catch((e) => {
				console.log(e)
			})
	}, [])

	const handleChange = (event, netState) => {
		setDashboardState(netState)
	}

	const handleBarGraphClick = (activeLabel) => {
		let filter
		switch (activeLabel) {
			case "פעיל":
				filter = "ACTIVE"
				break
			case "טיוטה":
				filter = "DRAFT"
				break
			case "ממתין לאישור":
				filter = "PENDINGREVIEW"
				break
			case "ממתין לעידכון":
				filter = "PENDINGUPDATE"
				break
			case "מבוטל":
				filter = "CANCELLED"
				break
			default:
				filter = "PENDINGREVIEW"
				break
		}
		navigate(`/?tab=posts&filter=${filter}`)
	}

	return (
		<Grid container spacing={3}>
			<Grid item xs={12}>
				<Box display={"flex"} justifyContent={"center"}>
					<ToggleButtonGroup disabled color="primary" value={dashboardState} exclusive onChange={handleChange}>
						<ToggleButton value="all">הכול</ToggleButton>
						<ToggleButton value="realtime">זמן אמת (24 שעות)</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Grid>
			<Grid item xs={12}>
				{loadingState.revenue && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 100,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.revenue && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "row",
							height: 100,
							justifyContent: "center",
						}}
					>
						<Typography component="h2" variant="h4" color="green" gutterBottom>
							סה״כ הכנסות:&nbsp;
						</Typography>
						<Typography component="p" variant="h4" color="green">
							{"\u20aa" + new Intl.NumberFormat().format(parseInt(revenue))}
						</Typography>
					</Paper>
				)}
			</Grid>
			<Grid item xs={12} md={4}>
				{loadingState.userCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.userCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
						}}
					>
						<Typography component="h2" variant="h6" color="darkgray" gutterBottom>
							משתמשים רשומים
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="darkgray"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=users")}
						>
							{userCount}
						</Typography>
						<Typography component="h2" variant="h6" color="primary" gutterBottom>
							משתמשים מאומתים
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="primary"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=users")}
						>
							{verifiedUserCount}
						</Typography>
					</Paper>
				)}
			</Grid>
			<Grid item xs={12} md={8}>
				{loadingState.postsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.postsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							width: "100%",
						}}
					>
						<Typography component="h2" variant="h6" color="primary" gutterBottom>
							סטטוס מודעות
						</Typography>
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								/* width={500}
							height={300} */
								data={postsCount}
								margin={{
									top: 5,
									/* right: 30,
								left: 20, */
									bottom: 5,
								}}
								onClick={(e) => {
									handleBarGraphClick(e.activeLabel)
								}}
							>
								<XAxis dataKey="name" />
								<YAxis width={20} tickMargin={10} allowDecimals={false} />
								<Tooltip />
								<Bar dataKey="count" fill="#8884d8" />
							</BarChart>
						</ResponsiveContainer>
					</Paper>
				)}
			</Grid>
			<Grid item xs={12} md={4}>
				{loadingState.dealsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.dealsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
						}}
					>
						<Typography component="h2" variant="h6" color="secondary" gutterBottom>
							סה״כ ללא עסקה
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="secondary"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=posts&filter=CLOSEDNODEAL")}
						>
							{noDealsCount}
						</Typography>
						<Typography component="h2" variant="h6" color="green" gutterBottom>
							סה״כ רכישה מיידית
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="green"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=posts&filter=CLOSEDDEAL")}
						>
							{buyNowDealsCount}
						</Typography>
						<Typography component="h2" variant="h6" color="green" gutterBottom>
							סה״כ מכירה פומבית
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="green"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=posts&filter=CLOSEDDEAL")}
						>
							{auctionDealsCount}
						</Typography>
					</Paper>
				)}
			</Grid>
			<Grid item xs={12} md={4}>
				{loadingState.bidsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.bidsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
						}}
					>
						<Typography component="h2" variant="h6" color="primary" gutterBottom>
							סה״כ הצעות במכירות פומביות
						</Typography>
						<Typography component="p" variant="h4" color="primary">
							{bidsCount}
						</Typography>
					</Paper>
				)}
			</Grid>
			<Grid item xs={12} md={4}>
				{loadingState.questionsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<CircularProgress />
					</Paper>
				)}
				{!loadingState.questionsCount && (
					<Paper
						sx={{
							p: 2,
							display: "flex",
							flexDirection: "column",
							height: 300,
						}}
					>
						<Typography component="h2" variant="h6" color="primary" gutterBottom>
							שאלות ממתינות לאישור
						</Typography>
						<Typography
							component="p"
							variant="h4"
							color="primary"
							sx={{ ":hover": { textDecoration: "underline", cursor: "pointer" } }}
							onClick={() => navigate("/?tab=questions")}
						>
							{pendingQuestionsCount}
						</Typography>
						<Typography component="h2" variant="h6" color="green" gutterBottom>
							שאלות מאושרות
						</Typography>
						<Typography component="p" variant="h4" color="green">
							{activeQuestionsCount}
						</Typography>
					</Paper>
				)}
			</Grid>
		</Grid>
	)
}

export default Dashboard
